import { watch } from 'vue';
import { createI18n } from 'vue-i18n';
import { toBcp47 } from './bcp47.js';
import {
  FALLBACK_LOCALE,
  eagerMessages,
  isLoadableLocale,
  localeFallbackChains,
  localeLoaders,
  resolveLocaleChain
} from './localeLoaders.js';

/**
 * Detect browser locale for pre-login experience
 * Maps browser language codes to our supported locale codes
 */
function detectBrowserLocale() {
  const browserLang = navigator.language.toLowerCase();

  // Direct mapping of browser locale to our locale codes
  const localeMap = {
    'en-us': 'en-us',
    'en-gb': 'en-gb',
    'en': 'en-us', // Default English to American
    'zh-cn': 'zh-cn',
    'zh-hk': 'zh-hk',
    'zh-tw': 'zh-hk', // Map Traditional Chinese to Cantonese
    'zh': 'zh-cn', // Default Chinese to Mandarin
    'es-mx': 'es-mx',
    'es-es': 'es-es',
    'es': 'es-mx', // Default Spanish to Mexican
    'fr-fr': 'fr-fr',
    'fr-ca': 'fr-ca',
    'fr': 'fr-fr', // Default French to France
    'de': 'de',
    'de-de': 'de',
    'ja': 'ja',
    'ja-jp': 'ja',
    'ko': 'ko',
    'ko-kr': 'ko',
    'pt-br': 'pt-br',
    'pt-pt': 'pt-pt',
    'pt': 'pt-br', // Default Portuguese to Brazilian
    'ru': 'ru',
    'ru-ru': 'ru',
    'ta': 'ta-in',
    'ta-in': 'ta-in',
    'it': 'it',
    'hi': 'hi',
    'vi': 'vi',
    'tr': 'tr',
    'nl': 'nl',
    'pl': 'pl',
    'id': 'id',
    'uk': 'uk',
    'el': 'el',
    'sv': 'sv',
    'no': 'no',
    'nb': 'no', // Norwegian Bokmål browser locale → our 'no'
    'nn': 'no', // Norwegian Nynorsk browser locale → our 'no'
    'da': 'da',
    'fi': 'fi',
    'bn': 'bn-in',
    'bn-in': 'bn-in',
    'te': 'te',
    'mr': 'mr'
  };

  // Try exact match first
  if (localeMap[browserLang]) {
    return localeMap[browserLang];
  }

  // Try language code without region (e.g., 'en' from 'en-au')
  const langCode = browserLang.split('-')[0];
  if (localeMap[langCode]) {
    return localeMap[langCode];
  }

  // Default to American English
  return 'en-us';
}

/**
 * Map language display name (from Firebase) to locale code
 * Example: "English (American)" → "en-us"
 */
export function mapLanguageNameToLocale(languageName) {
  const Languages = {
    'en-us': 'English (American)',
    'en-gb': 'English (British)',
    'zh-cn': 'Chinese (Mandarin)',
    'zh-hk': 'Chinese (Cantonese)',
    'es-mx': 'Spanish (Mexican)',
    'es-es': 'Spanish (European)',
    'fr-fr': 'French',
    'fr-ca': 'French (Canadian)',
    'de': 'German',
    'ja': 'Japanese',
    'ko': 'Korean',
    'pt-br': 'Portuguese (Brazilian)',
    'pt-pt': 'Portuguese (European)',
    'ru': 'Russian',
    'ta-in': 'Tamil (Indian)',
    'it': 'Italian',
    'hi': 'Hindi',
    'vi': 'Vietnamese',
    'tr': 'Turkish',
    'nl': 'Dutch',
    'pl': 'Polish',
    'id': 'Indonesian',
    'uk': 'Ukrainian',
    'el': 'Greek',
    'sv': 'Swedish',
    'no': 'Norwegian',
    'da': 'Danish',
    'fi': 'Finnish',
    'bn-in': 'Bengali (Indian)',
    'te': 'Telugu',
    'mr': 'Marathi'
  };

  // Find the locale code for the given language name
  for (const [code, name] of Object.entries(Languages)) {
    if (name === languageName) {
      return code;
    }
  }

  // Default to American English if not found
  return 'en-us';
}

/**
 * Compare two language codes by their base (pre-dialect) part.
 * Returns true if both share the same base — e.g. "en-us" and "en-gb" → true.
 * Returns false for null/undefined input on either side, even if both are nullish:
 * absence of a language is not "same language."
 */
export function isSameBaseLanguage(code1, code2) {
  if (!code1 || !code2) return false;
  const base1 = String(code1).toLowerCase().split('-')[0];
  const base2 = String(code2).toLowerCase().split('-')[0];
  return base1 === base2;
}

/**
 * Check localStorage for saved locale preference.
 * Guarded: storage can be unavailable (browser privacy modes throw on access;
 * test environments may lack a functional localStorage) and this runs at
 * module import, so a throw here would take down the whole app bundle.
 */
function getSavedLocale() {
  try {
    return localStorage.getItem('preferredLocale') || null;
  } catch (e) {
    return null;
  }
}

/**
 * Write the preferredLocale cache.
 *
 * Deliberately module-private: every write goes through applyLocale below, so
 * there is exactly one place that decides whether a write is allowed to happen.
 * Exporting this again would let a component write the cache without changing
 * the locale, which is the incoherence this file exists to prevent.
 *
 * Guarded for the same reason getSavedLocale is: storage access throws in some
 * privacy modes. The callers are async, so an unguarded throw here would
 * surface as an unhandled promise rejection rather than as a visible error, and
 * losing the cross-session cache is not worth failing a locale switch over.
 *
 * @param {string} locale - the locale that is actually live
 */
function cachePreferredLocale(locale) {
  try {
    localStorage.setItem('preferredLocale', locale);
  } catch (e) {
    console.warn('Could not cache the preferred locale:', e);
  }
}

/**
 * Drop a cache entry that no longer describes whoever is using this browser.
 * Guarded like the other two: removeItem throws in the same privacy modes.
 */
function clearPreferredLocale() {
  try {
    localStorage.removeItem('preferredLocale');
  } catch (e) {
    console.warn('Could not clear the preferred locale:', e);
  }
}

/**
 * Determine initial locale based on saved preference or browser detection
 */
function getInitialLocale() {
  return getSavedLocale() || detectBrowserLocale();
}

// Create i18n instance.
//
// It boots on the fallback locale with only en-us in `messages`: every other
// bundle is a lazily-loaded chunk (see localeLoaders.js), so the chosen locale
// cannot be applied synchronously at module import. `localeReady` below applies
// it as soon as its chunk lands, and vue-i18n's reactive `setLocaleMessage`
// re-renders anything already on screen.
const i18n = createI18n({
  legacy: false, // Use Composition API mode
  locale: FALLBACK_LOCALE,
  fallbackLocale: localeFallbackChains,
  messages: { ...eagerMessages },
  // Show warnings in development, suppress in production
  missingWarn: process.env.NODE_ENV !== 'production',
  fallbackWarn: process.env.NODE_ENV !== 'production'
});

// Bundles already handed to vue-i18n. Loading is idempotent and a bundle is
// only recorded here after setLocaleMessage succeeded, so a failed chunk is
// retried on the next attempt rather than being remembered as loaded.
const loadedLocales = new Set(Object.keys(eagerMessages));

// Most recently requested locale switch. A slow earlier request must not
// overwrite a later one: a user who picks Danish and then Japanese while both
// chunks are in flight ends on Japanese regardless of which response arrives
// first. The promise lets a superseded call resolve to the winner's result
// instead of to a transient mid-switch reading.
let latestSwitchToken = 0;
let latestSwitchPromise = Promise.resolve({
  applied: FALLBACK_LOCALE,
  abandoned: false,
});

// Loads that have been started but have not settled yet, keyed by locale.
//
// This is a dedupe table, not a log: an entry is removed as soon as its load
// settles, so the map holds at most one entry per locale and cannot grow with
// the number of switches. Two switches to the same dialect while its parent is
// still in flight share the one pending load rather than starting a second.
const inFlightLoads = new Map();

/**
 * Load one bundle into vue-i18n. Resolves true when the locale's messages are
 * available (already loaded, or fetched now), false when the chunk could not be
 * fetched. Never rejects.
 */
function loadLocaleBundle(locale) {
  if (loadedLocales.has(locale)) {
    return Promise.resolve(true);
  }
  const inFlight = inFlightLoads.get(locale);
  if (inFlight) {
    return inFlight;
  }
  const load = localeLoaders[locale];
  if (!load) {
    return Promise.resolve(false);
  }

  const attempt = (async () => {
    try {
      const messages = await load();
      i18n.global.setLocaleMessage(locale, messages);
      loadedLocales.add(locale);
      return true;
    } catch (error) {
      // Chunk fetch failed (offline, stale service worker, partial deploy). Do
      // not record it as loaded, so a later attempt can retry.
      console.warn(`Could not load locale bundle "${locale}":`, error);
      return false;
    } finally {
      inFlightLoads.delete(locale);
    }
  })();

  inFlightLoads.set(locale, attempt);
  return attempt;
}

/**
 * Resolves once every bundle currently being loaded has settled (loaded or
 * failed). Not needed to render: it exists so a caller that must observe a
 * fully-loaded fallback chain can await it deterministically instead of racing
 * the background parent fetches.
 *
 * Deliberately a snapshot of what is in flight *now* rather than an accumulating
 * promise: chaining each load onto the previous one would retain every earlier
 * load for the life of the page, and would let one stalled chunk wedge the
 * handle for every unrelated switch afterwards.
 *
 * A load that genuinely never settles does keep this pending, which is the
 * honest reading of "has everything finished loading". That costs nothing at
 * runtime: no app code awaits this, it is a determinism seam for tests and
 * diagnostics, and the render path never depends on a chain parent.
 */
export function chainLoadsSettled() {
  return Promise.allSettled([...inFlightLoads.values()]).then(() => {});
}

/**
 * Load `locale`, and start loading every locale it falls back to.
 *
 * Resolves true as soon as the requested bundle itself is live — it does NOT
 * wait for the chain parents. Parents only supply keys the requested bundle is
 * missing, and `setLocaleMessage` is reactive, so a parent that lands after the
 * switch upgrades those few strings in place. Awaiting them instead would mean a
 * parent chunk that *hangs* rather than fails (dropped connection, wedged
 * proxy) holds the entire UI in English until webpack's chunkLoadTimeout — 120s
 * by default — even though the user's own bundle was ready the whole time. That
 * is the same "wrong language for a long time, with no error" failure this unit
 * exists to remove, so the parent is treated as an upgrade, not a precondition.
 *
 * A parent that never arrives is degraded but usable: vue-i18n keeps walking the
 * chain down to the eager en-us.
 */
export async function loadLocaleMessages(locale) {
  const [primary, ...parents] = resolveLocaleChain(locale);

  // Started, not awaited. loadLocaleBundle never rejects, so these cannot become
  // unhandled rejections, and it registers each one in inFlightLoads before
  // returning, so chainLoadsSettled() can see them without anything being
  // retained after they settle.
  parents.forEach((parent) => {
    loadLocaleBundle(parent);
  });

  const primaryLoaded = await loadLocaleBundle(primary);
  return isLoadableLocale(locale) && primaryLoaded;
}

/**
 * Switch the active UI locale, loading its chunk first.
 *
 * Every assignment to `i18n.global.locale` goes through here. Assigning it
 * directly leaves the app on a locale whose messages were never fetched, which
 * vue-i18n reports as nothing at all: it silently renders English (or the raw
 * key) forever, with no error to notice.
 *
 * `abandoned` is reported separately from `applied` because "your locale did not
 * go live" has three causes and only one of them is about the cache. The chunk
 * failed (the request is unavailable, and the cache has to react); a later
 * switch won; or the reason for the switch stopped being true while its chunk
 * was in flight. In the last two the request was abandoned, this call speaks for
 * nobody any more, and touching the cache would be it overwriting a decision
 * that someone else has already made correctly.
 *
 * @param {string} locale - target locale code
 * @param {{isStillValid?: () => boolean}} [options] - re-checked immediately
 *   before the locale is applied, not just before the load starts. Chunk loads
 *   take arbitrarily long, so an account can change during one; a check that
 *   only runs up front is a check against the wrong moment.
 * @returns {Promise<{applied: string, abandoned: boolean}>} `applied` is the
 *   locale actually active afterwards: FALLBACK_LOCALE when the requested one
 *   could not be loaded, and the live locale when this switch was abandoned. It
 *   never claims success for a locale that is not live.
 */
function switchLocale(locale, { isStillValid } = {}) {
  const token = ++latestSwitchToken;

  const switchPromise = (async () => {
    const loaded = await loadLocaleMessages(locale);

    if (token !== latestSwitchToken) {
      // Superseded while this chunk was in flight. Defer to whichever switch is
      // now newest: tokens only increase, so that chain always terminates, and
      // reading i18n.global.locale.value here instead would report a transient
      // mid-switch value that a caller could then cache. The winner's own
      // `abandoned` flag is not this call's answer, so it is overridden.
      const winner = await latestSwitchPromise;
      return { applied: winner.applied, abandoned: true };
    }

    if (isStillValid && !isStillValid()) {
      // Typically: the account this locale was read for signed out while its
      // chunk was downloading. Applying it now would put the previous user's
      // language on the current one's screen, and there may be no later switch
      // to correct it (the incoming user's own profile read can still be in
      // flight, or their account may carry no nativeLanguage at all).
      return { applied: i18n.global.locale.value, abandoned: true };
    }

    i18n.global.locale.value = loaded ? locale : FALLBACK_LOCALE;
    return { applied: i18n.global.locale.value, abandoned: false };
  })();

  // Assigned synchronously, before the IIFE's first await resumes, so a
  // superseded call above always sees a strictly newer promise than its own.
  latestSwitchPromise = switchPromise;
  return switchPromise;
}

/**
 * Switch the active UI locale without touching the preferredLocale cache.
 *
 * @param {string} locale - target locale code
 * @returns {Promise<string>} the locale actually active afterwards
 */
export function setLocale(locale) {
  return switchLocale(locale).then((result) => result.applied);
}

/**
 * THE `preferredLocale` INVARIANT, in one place.
 *
 *   `localStorage.preferredLocale` is a CACHE of the locale derived from the
 *   signed-in user's `nativeLanguage`, except before login, where there is no
 *   account yet and it holds the visitor's own pick.
 *
 * `users/{uid}/nativeLanguage` (RTDB, an English display name) stays the single
 * source of truth. It is durable, cross-device and server-visible, and the
 * backend reads the same field for tutoring. `preferredLocale` exists only
 * because boot is synchronous and precedes auth: without it every non-English
 * user would get a flash of English on every load. It is never a third,
 * independent preference, and nothing may write it except through here.
 *
 * Two consequences worth stating, because both look like bugs from the outside:
 *
 *  - `loadUserProfile()` overwriting the cache from `nativeLanguage` is correct.
 *    That is a cache being refreshed from truth.
 *  - Showing the UI in another language WITHOUT changing `nativeLanguage` is a
 *    preview, not a preference, so it must not be cached. Otherwise the cache
 *    points somewhere the account does not, with nothing to correct it until the
 *    next profile load.
 *
 * @param {string} requested - the locale code to apply
 * @param {{cache: boolean}} options - whether this call is entitled to refresh
 *   the cache, i.e. whether it represents truth (or, pre-login, the only thing
 *   standing in for it)
 * @returns {Promise<string>} the locale actually live afterwards
 */
async function applyLocale(requested, { cache, isStillValid }) {
  const { applied, abandoned } = await switchLocale(requested, { isStillValid });

  if (!cache || abandoned) {
    // Abandoned means a later switch won, or the account this was requested for
    // is no longer signed in. Either way `applied` is somebody else's locale,
    // not this call's, and whoever owns it is responsible for its cache entry.
    // Writing from here is how a stale caller "corrects" a cache it knows
    // nothing about, which is the bug in a different costume.
    return applied;
  }

  if (applied === requested) {
    cachePreferredLocale(applied);
  } else if (getSavedLocale() !== requested) {
    // The chunk could not be fetched, so the request is not live and must not
    // be recorded as if it were. Two sub-cases, and only one of them writes:
    //
    //  - the cache ALREADY holds `requested` (a good stored preference whose
    //    chunk happened to 404 on this load). Leave it. Overwriting it with the
    //    en-us fallback would destroy a real preference over a transient network
    //    failure, and for a logged-out visitor there is no nativeLanguage to
    //    heal it from later.
    //  - the cache holds something else entirely: an earlier session's locale,
    //    typically the previous user on a shared browser. That value describes
    //    nobody who is here now, so keeping it would start the next boot in a
    //    language this user never asked for. Drop it and let boot fall back to
    //    browser detection, which is neutral rather than wrong.
    clearPreferredLocale();
  }
  return applied;
}

/**
 * Adopt the locale implied by the signed-in user's `nativeLanguage`, and refresh
 * the cache from it. This is the cache being written from truth.
 *
 * @param {string} languageName - the RTDB display name, e.g. "English (American)"
 * @param {{isStillValid?: () => boolean}} [options] - re-checked once the chunk
 *   has landed and before anything is applied, so a profile read for a user who
 *   has since signed out cannot land on the next user's session
 * @returns {Promise<string>} the locale actually live afterwards
 */
export function applyNativeLanguageLocale(languageName, { isStillValid } = {}) {
  return applyLocale(mapLanguageNameToLocale(languageName), {
    cache: true,
    isStillValid,
  });
}

/**
 * Adopt a logged-out visitor's explicit language pick, and cache it.
 *
 * Legitimate only where no account exists yet: there is no `nativeLanguage` to
 * derive from, so the pick is the best available stand-in for truth and has to
 * survive navigation. `AppNavigation` mounts the only such control
 * (`LanguageSwitcher`) behind `v-if="!isLoggedIn"`.
 *
 * @param {string} locale - the locale code the visitor picked
 * @returns {Promise<string>} the locale actually live afterwards
 */
export function applyVisitorLocalePick(locale) {
  return applyLocale(locale, { cache: true });
}

/**
 * Show the UI in `locale` without claiming it is the user's language.
 *
 * For a signed-in surface that re-renders itself in another language so the user
 * can read it, while `nativeLanguage` is unchanged. Caching here is what made
 * `LanguageConflictModal` incoherent: its dropdown wrote the cache, its submit
 * button wrote RTDB, and neither called the other.
 *
 * @param {string} locale - the locale code to show
 * @returns {Promise<string>} the locale actually live afterwards
 */
export function previewLocale(locale) {
  return applyLocale(locale, { cache: false });
}

/**
 * The locale the UI is actually rendered in right now.
 *
 * For non-Vue callers that need to tell something outside the app which
 * language the user is reading -- today, the Stripe hosted pages, which
 * otherwise follow the browser and can disagree with the app on screen.
 *
 * Deliberately reads the LIVE locale rather than `preferredLocale` or
 * `nativeLanguage`: those two can both be ahead of what is on screen (a chunk
 * that failed to load leaves the app in English while the cache says Danish),
 * and a checkout page in a language the app itself could not render is a worse
 * answer than one that matches what the user is looking at.
 *
 * `switchLocale` only ever assigns a code whose bundle is loaded, so this is
 * always one of the shipped locale codes -- never a browser tag and never
 * undefined.
 *
 * @returns {string} the live locale code, e.g. "en-us", "da", "zh-hk"
 */
export function getActiveLocale() {
  return i18n.global.locale.value;
}

/**
 * Drop the cache because there is no longer an account for it to be a cache of.
 *
 * Called on sign-out. The invariant admits exactly two contents: a signed-in
 * user's `nativeLanguage`, or a logged-out visitor's own pick. After a sign-out
 * the stored value is neither. Left in place it is the previous user's language,
 * and on a shared browser it would greet the next person in it -- indefinitely,
 * if their account carries no `nativeLanguage` yet for `loadUserProfile` to
 * correct it from.
 *
 * The live locale is deliberately NOT reset: the person who just signed out is
 * still looking at the screen, and flipping the page they land on into a
 * different language is a worse answer than one English frame on their next
 * visit.
 */
export function forgetCachedLocale() {
  clearPreferredLocale();
}

/**
 * Keep `<html lang>` on the locale that is actually live.
 *
 * `public/index.html` ships `<html lang="">`, so without this every page claims
 * no language at all: a screen reader picks its default voice regardless of what
 * the user is reading, browsers pick the wrong hyphenation and CJK font
 * fallback, and "translate this page" has nothing to translate from.
 *
 * A WATCHER on the locale, deliberately, and never the router's `beforeEach`:
 *
 *  - that hook also runs every auth guard in this app, so a throw inside it does
 *    not degrade to a wrong `lang` attribute, it breaks all navigation;
 *  - it only fires on navigation, so switching language from the account page
 *    (where the switcher lives, and where no route change follows) would leave
 *    the attribute describing the previous language indefinitely.
 *
 * Reads `i18n.global.locale`, which switchLocale only ever sets to a locale
 * whose messages are loaded, so the attribute cannot advertise a language the
 * page is not actually rendered in. `immediate` covers the boot frame, before
 * any switch has happened.
 *
 * Guarded on `document` because this module is imported by node-environment unit
 * tests and by any non-DOM consumer; there is no server-side render path today,
 * and an unguarded reference would make the module itself unimportable there.
 */
if (typeof document !== 'undefined') {
  watch(
    i18n.global.locale,
    (locale) => {
      document.documentElement.lang = toBcp47(locale);
    },
    { immediate: true }
  );
}

/**
 * Resolves once the initial locale has been applied (or has failed and fallen
 * back to English). The app does not block mount on this: it renders English
 * first and swaps in the real locale when the chunk lands, so a slow or failed
 * locale fetch can never leave the user staring at a blank screen.
 */
export const localeReady = setLocale(getInitialLocale());

export default i18n;
